/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useState, useEffect, forwardRef } from 'react';
import { TextField, InputAdornment, TextFieldProps } from '@mui/material';
import PercentIcon from '@mui/icons-material/Percent';

interface PercentageInputProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
  value?: number | string;
  onChange?: (value: number) => void;
  maxValue?: number;
  minValue?: number;
  decimals?: number;
}

const PercentageInput = forwardRef<HTMLDivElement, PercentageInputProps>(({
  value = 0,
  onChange,
  maxValue = 100,
  minValue = 0,
  decimals = 2,
  ...textFieldProps
}, ref) => {
  const [displayValue, setDisplayValue] = useState('');
  const [focused, setFocused] = useState(false);

  const formatPercentage = (num: number): string => {
    if (isNaN(num)) return '0' + (decimals > 0 ? '.' + '0'.repeat(decimals) : '');
    return num.toFixed(decimals);
  };
  const parseValue = (str: string): number => {
    const cleaned = str.replace(/[^\d.-]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  };
  useEffect(() => {
    const numValue = typeof value === 'string' ? parseValue(value) : (value || 0);
    if (!focused) {
      setDisplayValue(formatPercentage(numValue));
    }
  }, [value, focused, decimals]);

  const handleFocus = () => {
    setFocused(true);
    const numValue = typeof value === 'string' ? parseValue(value) : (value || 0);
    
    if (numValue === 0 || numValue < minValue || numValue > maxValue) {
      setDisplayValue('');
    } else {
      const cleanValue = numValue.toString().replace(/^0+(?=\d)/, '');
      setDisplayValue(cleanValue);
    }
  };

  const handleBlur = () => {
    setFocused(false);
    
    if (displayValue === '') {
      setDisplayValue(formatPercentage(0));
      if (onChange) {
        onChange(0);
      }
      return;
    }
    
    const numValue = parseValue(displayValue);
    const clampedValue = Math.max(minValue, Math.min(maxValue, numValue));
    
    setDisplayValue(formatPercentage(clampedValue));
    
    if (onChange) {
      onChange(clampedValue);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    
    if (focused) {
      if (inputValue === '' || /^\d*\.?\d*$/.test(inputValue)) {
        const numValue = parseValue(inputValue);
        if (inputValue === '' || numValue <= maxValue || inputValue.endsWith('.')) {
          
          let cleanedValue = inputValue;
          if (/^0+\d/.test(inputValue)) {
            cleanedValue = inputValue.replace(/^0+/, '');
          }
          if (/^0*\./.test(inputValue)) {
            cleanedValue = inputValue.replace(/^0+/, '0');
          }
          
          setDisplayValue(cleanedValue);
        }
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }
    if (event.ctrlKey && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) {
      return;
    }
    if (/^\d$/.test(event.key)) {
      return;
    }
    if (event.key === '.' && decimals > 0 && !displayValue.includes('.')) {
      return;
    }
    event.preventDefault();
  };

  return (
    <TextField
      {...textFieldProps}
      ref={ref}
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <PercentIcon />
          </InputAdornment>
        ),
        className: 'percentage-input-props',
        ...textFieldProps.InputProps,
      }}
      inputProps={{
        inputMode: 'decimal',
        pattern: '[0-9]*[.,]?[0-9]*',
        ...textFieldProps.inputProps,
      }}
    />
  );
});

PercentageInput.displayName = 'PercentageInput';

export default PercentageInput;